import { computed, IObservableArray, observable } from "mobx";
import { observer, Provider } from "mobx-react";
import * as qs from "query-string";
import * as React from "react";
import { RouteComponentProps } from "react-router-dom";
import { ResetFiltersButton } from "../Buttons/ResetButton";
import { ResultSetSizeButton } from "../Buttons/ResultSetSizeButton";
import { ShareButton } from "../Buttons/ShareButton";
import { ShowAreaAsideButton } from "../Buttons/ShowAreaAsideButton";
import { ViewCard } from "../Buttons/ViewCard";
import { ViewList } from "../Buttons/ViewList";
import { DocumentList } from "../Document/DocumentList";
import { DocumentPreviews } from "../Document/DocumentPreviews";
import * as DocumentReader from "../DocumentReader";
import { Area } from "../General/Area";
import { AreaAside } from "../General/AreaAside";
import { AreaBody } from "../General/AreaBody";
import { Toolbar } from "../General/Toolbar";
import { TypeAheadSearchInput } from "../General/TypeAheadSearchInput";
import { WizardBox, WizardBoxType } from "../General/WizardBox";
import { AppStore } from "../Stores/AppStore";
import { DialogStore } from "../Stores/DialogStore";
import { DocumentFilter as DocumentFilterStore, IDocumentFilter } from "../Stores/DocumentFilterStore";
import { DocumentSearchStore } from "../Stores/DocumentSearchStore";
import { PreviewStore } from "../Stores/PreviewStore";
import { Strings } from "../Stores/StringsStore";
import { UiRepos } from "../Stores/UiStore";
import * as Utilities from "../Utilities";
import { AdditionalFilterValuesSelection } from "./AdditionalFilterValuesSelection";
import { DocumentFilter, isSelected } from "./DocumentFilter";

interface IDocumentSearchProps {
	store?: IDocumentFilter;
	onDocumentClick?: (id: string) => void;
	isSelected?: (id: string) => boolean;
	currentDocument?: string;
	isShiftKey?: boolean;
	updateSearchResult: (ids: string[]) => void;
}

interface IDocumentSearchRouteProps extends RouteComponentProps<any>, IDocumentSearchProps { }

@observer
export class DocumentSearch extends React.Component<IDocumentSearchRouteProps, {}> {

	filterStore = this.props.store ? this.props.store : DocumentFilterStore;
	store: DocumentSearchStore = new DocumentSearchStore(this.filterStore, this.props.updateSearchResult);
	previewStore: PreviewStore = new PreviewStore("reader");

	searchTimeoutHandle: number;
	@observable loading: boolean = false;
	wasCollapsed: boolean;

	componentDidMount() {
		if (!this.props.onDocumentClick) {
			AppStore.setTitle(Strings.reader.title);
		}
		this.store.loadUiSettings();
		this.store.areaUiInfo.contentGroupAccordionStatus.replace(UiRepos.readerUiInfo.contentGroupAccordionStatus);
		this.useSearchCookies();

		//assign the searchTerm passed through query string from home to reader - to store `searchTerm` variable.
		const parsedQuery = qs.parse(this.props.location.search);
		if (parsedQuery && parsedQuery.searchTerm) {
			this.store.searchTerm = this.store.typeAheadStore.searchTerm = parsedQuery.searchTerm;
		}
		else if (this.store.searchTerm) {
			this.store.typeAheadStore.searchTerm = this.store.searchTerm;
		}
		else {
			this.store.searchTerm = this.store.typeAheadStore.searchTerm = "";
		}

		this.previewStore.loadSettings();
		this.store.triggerSearch(false);
		this.store.typeAheadStore.suggestions = [];
		window.addEventListener('orientationchange', this.handleOrientation);
	}

	componentDidUpdate() {
		if (!this.props.onDocumentClick) {
			AppStore.setTitle(Strings.reader.title);
		}
	}

	componentWillUnmount() {
		window.removeEventListener("orientationchange", this.handleOrientation);
	}

	useSearchCookies() {
		let cookie = "; " + document.cookie;
		let cookies = cookie.split("; ").filter(c => c.indexOf("Noxum.Search.") !== -1);
		if (cookies.length === 1 && cookies[0].split("=")[0] === "Noxum.Search.NoResult") {
			DialogStore.showError(Strings.reader.deepLinkNotFoundTitle, Strings.reader.deepLinkNotFoundText);
			document.cookie = "Noxum.Search.NoResult=; expires=Thu, 01 Jan 1970 00:00:01 GMT;";
		}
		else if (cookies.length > 0) {
			this.store.searchTerm = "";
			this.filterStore.clearFilter();
			cookies.forEach(c => {
				c = decodeURIComponent(c);
				let cookieName = c.split('=')[0];
				try {
					let name = cookieName.replace("Noxum.Search.", "");
					let values = c.split('=')[1].split(',');
					if (name === "__searchterm") {
						this.store.searchTerm = values[0];
					}
					else {
						let filter = new DocumentReader.DocumentFilter();
						filter.name = name;
						values.forEach(v => filter.values.push(decodeURIComponent(v)));
						this.filterStore.setFilter(filter);
					}
				}
				finally {
					document.cookie = cookieName + "=; expires=Thu, 01 Jan 1970 00:00:01 GMT;";
				}
			});
		}
	}

	/*----------event Handlers -----------*/


	//when user clicks reset button
	resetFilterSettings = () => {
		this.store.resetPaginationParams();
		this.store.resetFilter();
		this.store.searchTerm = "";
		this.store.typeAheadStore.searchTerm = "";
		this.store.typeAheadStore.documentSearchStore.searchTerm = "";
		this.store.triggerSearch(false);
	}

	handleOrientation = () => {
		if (screen.width > screen.height) {
			if (screen.width >= 880 && this.store.areaUiInfo.isCollapsed) {
				this.store.areaUiInfo.isCollapsed = false;
				this.wasCollapsed = true;
			}
		}
		else if (!this.store.areaUiInfo.isCollapsed && this.wasCollapsed) {
			this.store.areaUiInfo.isCollapsed = true;
			this.wasCollapsed = false;
		}
	}

	@computed get areaAsideButtonTooltip() {
		return this.store.areaUiInfo.isCollapsed ? Strings.reader.areaAsideButtonCollapsedTooltip : Strings.reader.areaAsideButtonExpandedTooltip;
	}

	getDeepLink = () => {
		DocumentReader.createDeepLinkAsync(this.store.currentDocumentQuery)
			.then(result => {
				DialogStore.showDeepLink(result);
			})
			.catch(error => {
				DialogStore.showError(Strings.general.errorDialogTitle, error);
			});
	}

	refs: {
		[key: string]: HTMLElement;
		txtDocumentSearch: HTMLElement;
	}

	render() {
		let fileCountText = this.store.fileCount === 1 ? Strings.reader.resultSetSizeTextSingle : this.store.fileCount === -1 ? Strings.reader.searchingText : Strings.reader.resultSetSizeTextMultiple;
		let asideComponent = <AreaAside>
			<Toolbar>
				<TypeAheadSearchInput searchStore={this.store} buttonAlignment={1} />
				<ResetFiltersButton onClick={this.resetFilterSettings} disabled={!this.store.documentSearchFilters.some(f => f.values.some(v => isSelected(v))) && !this.store.searchTerm} testId="resetButton" />
			</Toolbar>

			<div className="area-grid__aside-content is--reset-gap" data-test-id="filterList" id="filterList">
				<div className="filtergroup is--group-open">
					<div className="filtergroup-content">
						<ResultSetSizeButton onClick={this.store.areaUiInfo.selectCollapse} label={Utilities.format(fileCountText, this.store.fileCount)} testId="resultSetSizeButton" />
					</div>
				</div>
				<DocumentFilters store={this.store} documentSearchFilters={this.store.documentSearchFilters} />
			</div>

			{this.store.additionalFilterValuesWizardState.isOpen ? <WizardBox context={this.store.additionalFilterValuesWizardState} type={WizardBoxType.ConfirmReject}>
				<AdditionalFilterValuesSelection />
			</WizardBox> : null}
		</AreaAside>;

		return <Provider store={this.store} areaUiInfo={this.store.areaUiInfo}>
			<Area asideComponent={asideComponent}>
				<AreaBody>
					<Toolbar>
						<span className="buttongroup is--buttongroup-left">
							<ShowAreaAsideButton title={this.areaAsideButtonTooltip} />
							{(!AppStore.context.sessionDetails.isApp || AppStore.isSystemOnline) && !this.props.onDocumentClick ? <ShareButton onClick={this.getDeepLink} title={Strings.reader.shareDeepLinkButtonTooltip} testId="deepLinkButton" /> : null}
						</span>
						{!this.props.onDocumentClick ? <span className="buttongroup is--buttongroup-right">
							<ViewList store={this.previewStore} />
							<ViewCard store={this.previewStore} />
						</span> : null}
					</Toolbar>
					{!this.previewStore.showPreviews || this.props.onDocumentClick
						? <DocumentList showValidities={true} onDocumentClick={this.props.onDocumentClick} isSelected={this.props.isSelected}
							currentDocument={this.props.currentDocument} />
						: <DocumentPreviews />}
				</AreaBody>
			</Area>
		</Provider>;
	}
}

interface IDocumentFiltersProps {
	documentSearchFilters: IObservableArray<DocumentReader.DocumentSearchFilter>;
	store: DocumentSearchStore;
}

class DocumentFilters extends React.Component<IDocumentFiltersProps, {}> {

	lastFilter: string;
	lastPosition: number;
	filtersLoaded: boolean;

	componentDidUpdate() {
		if (this.filtersLoaded === false) {
			this.filtersLoaded = true;
		}
		if (this.lastFilter && this.lastPosition && this.filtersLoaded) {
			this.scroll();
		}
	}

	scroll = () => {
		let element = document.getElementById(this.lastFilter); 
		if (element && element.parentElement) {
			let parent = element.parentElement;
			let currentPosition = element.getBoundingClientRect().top;
			parent.scrollTop = parent.scrollTop + (currentPosition - this.lastPosition);
			this.lastFilter = null;
			this.lastPosition = null;
		}
	}

	onSelect = (filter: string, position: number) => {
		this.lastFilter = filter;
		this.lastPosition = position;
		this.filtersLoaded = false;
	}

	render() {
		let displayFilters = this.props.documentSearchFilters.filter(filter => filter.name === "lang" || filter.name === "mimetype" || filter.isHierarchy ||
			filter.values.some(value => value.occurrences > 0 || value.isSelected || value === this.props.store.lastDeselectedFilterValue));
		return <div className="area-grid__aside-content" data-test-id="filterList">
			{
				displayFilters.map(filter => {
					return <DocumentFilter key={filter.name} filter={filter} testId={filter.name} uiContext={this.props.store.areaUiInfo} testName="documentFilter"
						onSelect={this.onSelect} />;
				})
			}
		</div>;
	}
}